import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { BookingProvider } from "@/context/BookingContext";
import { SmoothScroll } from "@/components/SmoothScroll";
import { BookingDialog } from "@/components/BookingDialog";
import { PolicyBanner } from "@/components/PolicyBanner";
import { ContactWidget } from "@/components/ContactWidget";
import PageTracker from "@/components/PageTracker";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import SignAgreement from "@/pages/SignAgreement";
import ContactPage from "@/pages/ContactPage";
import ReviewPage from "@/pages/ReviewPage";
import PayPage from "@/pages/PayPage";
import SaveCardPage from "@/pages/SaveCardPage";
import CardSetup from "@/pages/CardSetup";
import ReservePay from "@/pages/ReservePay";
import ReservationPolicy from "@/pages/ReservationPolicy";
import DriverPortal from "@/pages/DriverPortal";
import CompleteBooking from "@/pages/CompleteBooking";
import UploadDocs from "@/pages/UploadDocs";
import PreReturn from "@/pages/PreReturn";
import InsuranceUpload from "@/pages/InsuranceUpload";
import DocUploadPortal from "@/pages/DocUploadPortal";
import InspectionReport from "@/pages/InspectionReport";
import AccidentReport from "@/pages/AccidentReport";
import ReservationRequest from "@/pages/ReservationRequest";
import PrivateBooking from "@/pages/PrivateBooking";
import ExtendRequest from "@/pages/ExtendRequest";
import Rebook from "@/pages/Rebook";
import RebookOffer from "@/pages/RebookOffer";
import WeeklySpecial from "@/pages/WeeklySpecial";
import DriversClub from "@/pages/DriversClub";
import ClubDashboard from "@/pages/ClubDashboard";
import Receipt from "@/pages/Receipt";

const SpecialRedirect = () => {
  const loc = useLocation();
  return <Navigate to={`/weekly-special${loc.search}`} replace />;
};

const TitleManager = () => {
  const loc = useLocation();
  useEffect(() => {
    document.title = loc.pathname.startsWith("/admin")
      ? "AutoHire STL — Admin Control Center"
      : "AutoHire STL — Affordable Car Rentals in St. Louis";
  }, [loc.pathname]);
  return null;
};

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <BookingProvider>
        <BrowserRouter>
          <TitleManager />
          <PageTracker />
          <PolicyBanner />
          <SmoothScroll>
            <Routes>
              <Route path="/" element={window.location.hostname.startsWith("special.") ? <WeeklySpecial /> : <Home />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/sign/:token" element={<SignAgreement />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/review" element={<ReviewPage />} />
              <Route path="/pay" element={<PayPage />} />
              <Route path="/save-card" element={<SaveCardPage />} />
              <Route path="/card-setup/:token" element={<CardSetup />} />
              <Route path="/reserve-pay/:token" element={<ReservePay />} />
              <Route path="/reservation-policy" element={<ReservationPolicy />} />
              <Route path="/drivers" element={<DriverPortal />} />
              <Route path="/drivers/:token" element={<DriverPortal />} />
              <Route path="/complete/:token" element={<CompleteBooking />} />
              <Route path="/upload-docs/:token" element={<UploadDocs />} />
              <Route path="/pre-return/:token" element={<PreReturn />} />
              <Route path="/insurance-upload/:token" element={<InsuranceUpload />} />
              <Route path="/customer-docs/:token" element={<DocUploadPortal />} />
              <Route path="/inspection/:token" element={<InspectionReport />} />
              <Route path="/accident-report/:token" element={<AccidentReport />} />
              <Route path="/request" element={<ReservationRequest />} />
              <Route path="/book/:token" element={<PrivateBooking />} />
              <Route path="/extend/:token" element={<ExtendRequest />} />
              <Route path="/rebook/:token" element={<Rebook />} />
              <Route path="/rebook-offer/:token" element={<RebookOffer />} />
              <Route path="/receipt/:token" element={<Receipt />} />
              <Route path="/weekly-special" element={<WeeklySpecial />} />
              <Route path="/special" element={<SpecialRedirect />} />
              <Route path="/drivers-club" element={<DriversClub />} />
              <Route path="/club/dashboard" element={<ClubDashboard />} />
              <Route path="/club" element={<Navigate to="/drivers-club" replace />} />
            </Routes>
          </SmoothScroll>
          <BookingDialog />
          <ContactWidget />
        </BrowserRouter>
        <Toaster position="top-center" theme="dark" richColors />
      </BookingProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
